import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { finalize, shareReplay } from 'rxjs/operators';
import { User, GroupMemberLight } from '../../user/model/user';
import { Cacheable, CacheBuster } from 'ts-cacheable';
import { Subject } from 'rxjs';
import { MessengerService } from './messenger.service';
import { ForgotPassword } from '../model/forgot-password';
import { SendEmailSMSContent } from 'src/app/shared/community-service-info/email-sms-content';
import { UserPasswordExpiration } from '../model/userPasswordExpiration';
import { OnlineUserListResponse } from 'src/app/user/model/online-user-summary';

const userCacheBuster = new Subject<void>();

/**
 * Provides data access to the User API.
 */
@Injectable({ providedIn: 'root' })
export class UserService implements OnDestroy
{
  /**
   * Localized roles.
   */
  public roles: Array<{ text: string; value: string; width: number }> = [
    { text: 'Care Coordinator', value: 'ROLE_CARE_COORDINATOR', width: 85 },                  // Care Coordinator access level
    { text: 'Program Lead', value: 'ROLE_PROGRAM_LEAD', width: 75 },                          // Program Lead access level
    { text: 'System Operator', value: 'ROLE_SYSTEM_OPERATOR', width: 0 },                     // Restrictive Admin access level
    { text: 'Operator', value: 'ROLE_OPERATOR', width: 55 },                                  // All access level
    { text: 'Community Admin', value: 'ROLE_COMMUNITY_ADMIN', width: 0 },                     // Restrictive Community Admin access level
    { text: 'Community Coordinator', value: 'ROLE_COMMUNITY_COORDINATOR', width: 112 },       // Restrictive Community Coordinator access level
    { text: 'Community User', value: 'ROLE_COMMUNITY_USER', width: 0 },                       // Community Portal (Patient) access level
    { text: 'Referral Coordinator', value: 'ROLE_REFERRAL_COORDINATOR', width: 98 },          // Restrictive Referral Coordinator access level
    { text: 'Directory Manager', value: 'ROLE_DIRECTORY_MANAGER', width: 90 },                // Restrictive Community Services Directory Manager access level
    { text: 'Data Analyst', value: 'ROLE_DATA_ANALYST', width: 70 },                          // Restrictive Data Analyst access level
    { text: 'Revenue Manager Operator', value: 'ROLE_REVENUE_MANAGER_OPERATOR', width: 105 }, // Revenue Manager Operator access level
    { text: 'Revenue Manager Analyst', value: 'ROLE_REVENUE_MANAGER_ANALYST', width: 100 },   // Restrictive Revenue Manager Analyst access level
    { text: 'Revenue Analyst', value: 'ROLE_CBO_REVENUE_ANALYST', width: 67 },                // Restrictive Revenue Analyst access level
    { text: 'Program Support', value: 'ROLE_PROGRAM_SUPPORT', width: 85 },                    // Program Support access level
  ];

  // Do not change value Spanish unless back-end is being changed.
  // text also remains in the current selected language. Only login dropdown is showing translated languages.
  public preferredLanguages: Array<{ text: string; value: string }> = [
    { text: 'English', value: 'English' },
    { text: 'Spanish', value: 'Spanish' }
  ];

  /** Coalesces concurrent GET /users/online calls into a single in-flight request. */
  private onlineUsersInflightRequest: Observable<OnlineUserListResponse> | null = null;

  private messagingLanguageChangedSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private messengerService: MessengerService)
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  /**
   * Delete a user.
   *
   * @param id User id
   */
  @CacheBuster(
    {
      cacheBusterNotifier: userCacheBuster
    })
  public deleteUser(id: number): Observable<User>
  {
    return this.http.delete<User>(`/users/${id}`);
  }

  /**
   * Persist a User in the database.
   *
   * @param data Data to save
   * @param isNew Is this a new User?
   */
  @CacheBuster(
    {
      cacheBusterNotifier: userCacheBuster
    })
  public saveUser(data: User, isNew: boolean, userId?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/users', data);
    }
    else
    {
      return this.http.put(`/users/${userId}`, data);
    }
  }

  /**
   * Get users of a specified role.
   *
   * @param role Role of the user.
   */
  @Cacheable()
  public getCacheUsersByRole(role: string): Observable<User[]>
  {
    return this.http.get<any>(`/users/role/${role}`);
  }

  /**
   * Get a user by case id
   *
   * @param caseId Case Id
   */
  @Cacheable()
  public getCacheUserByCase(caseId: number): Observable<any>
  {
    return this.http.get<any>(`/users/user/case/${caseId}`);
  }

  /**
   * Get users who a members of the specified group.
   *
   * @param groupId Group ID.
   */
  @Cacheable()
  public getCacheUsersByChannelGroups(channelId: number): Observable<User[]>
  {
    return this.http.get<any>(`/users/channel/${channelId}`);
  }

  @Cacheable(
    {
      cacheBusterObserver: userCacheBuster
    })
  public getCachedPhotoUser(username: string): Observable<any>
  {
    return this.http.get<any>(`/users/photo/${username}`);
  }

  /**
   * Get all users.
   */
  @Cacheable(
    {
      cacheBusterObserver: userCacheBuster
    })
  public getCachedUsers(allUsers = false, addCommunityUser = false, memberId = null, includeUserPhoto = false, roleAccess: string = '', dataLoad: string = ''): Observable<User[]>
  {
    let params = allUsers ? '?all=true' : '';
    if (addCommunityUser && memberId !== null)
    {
      params += params !== '' ? '&' : '?';
      params += `addCommunityUser=true&memberId=${memberId}`;
    }
    if (includeUserPhoto)
    {
      params += params !== '' ? '&' : '?';
      params += `includeUserPhoto=true`;
    }
    if (roleAccess !== '')
    {
      params += params !== '' ? '&' : '?';
      params += `roleAccess=${roleAccess}`;
    }
    if (dataLoad !== '')
    {
      params += params !== '' ? '&' : '?';
      params += `dataLoad=${dataLoad}`;
    }
    return this.http.get<User[]>(`/users${params}`);
  }

  @CacheBuster(
    {
      cacheBusterNotifier: userCacheBuster
    })
  public temporarilyDisableUsers(userIds: Array<number>): Observable<any>
  {
    return this.http.put<any>('/users/temporarily/disable', userIds);
  }

  @CacheBuster(
    {
      cacheBusterNotifier: userCacheBuster
    })
  public reEnableTemporarilyDisabledUsers(): Observable<any>
  {
    return this.http.put<any>('/users/temporarily/enable', null);
  }
  
  /**
   * Lightweight staff candidates for group edit combobox ({@code GET /users?dataLoad=light}).
   */
  @Cacheable(
    {
      cacheBusterObserver: userCacheBuster
    })
  public getCachedLightStaffCandidates(): Observable<GroupMemberLight[]>
  {
    return this.http.get<GroupMemberLight[]>(`/users?dataLoad=light`);
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get a user.
   *
   * @param id User id
   */
  public getUserById(id: number, loadPhoto: boolean): Observable<any>
  {
    if (loadPhoto)
    {
      return this.http.get<any>(`/users/${id}`);
    }
    else
    {
      return this.http.get<any>(`/users/${id}?loadPhoto=false`);
    }
  }

  /**
   * Lightweight members of a group (excludes deleted, includes disabled).
   */
  public getLightGroupMembers(groupId: number): Observable<GroupMemberLight[]>
  {
    return this.http.get<GroupMemberLight[]>(`/users/groups/${groupId}`);
  }

  /**
   * Get a user.
   *
   * @param email User email
   */
  public getUserByEmail(email: string, loadPhoto: boolean): Observable<any>
  {
    if (loadPhoto)
    {
      return this.http.get<any>(`/users/email/${email}`);
    }
    else
    {
      return this.http.get<any>(`/users/email/${email}?loadPhoto=false`);
    }
  }

  /**
   * Get a user QRCode.
   *
   * @param id User id
   */
  public getUserQRCodeById(id: number, generateNew: boolean): Observable<any>
  {
    let queryString = '';
    if (generateNew !== null && generateNew)
    {
      queryString = `?generateNew=${generateNew}`;
    }
    return this.http.get<any>(`/users/${id}/qrcode${queryString}`);
  }

  /**
   * Get a user by username
   *
   * @param username Username
   */
  public getUserByUsername(username: string): Observable<any>
  {
    return this.http.get<any>(`/users/username/${username}`);
  }

  /**
   * Get a user by member id
   *
   * @param memberId Member Id
   */
  public getUserByMember(memberId: number): Observable<any>
  {
    return this.http.get<any>(`/users/user/member/${memberId}`);
  }

  /**
   * Get a user by task ad-hoc id
   *
   * @param memberId Member Id
   */
  public getUserByAdHocTask(taskId: string): Observable<any>
  {
    return this.http.get<any>(`/users/user/task/${taskId}`);
  }

  /**
   * Get a user by case id
   *
   * @param caseId Case Id
   */
  public getUserByCase(caseId: number): Observable<any>
  {
    return this.http.get<any>(`/users/user/case/${caseId}`);
  }

  /**
   * Get users who a members of the specified group.
   *
   * @param groupId Group ID.
   */
  public getUsersByChannelGroups(channelId: number): Observable<User[]>
  {
    return this.http.get<any>(`/users/channel/${channelId}`);
  }

  /**
   * Get the users who are members of the channel staff group using the case.
   *
   * @param caseId Case ID.
   */
  public getUsersByCase(caseId: string): Observable<User[]>
  {
    return this.http.get<User[]>(`/users/case/${caseId}`);
  }

  public addUserToChannel(userId: number, channelId: number): Observable<any>
  {
    return this.http.put<any>(`/users/${userId}/addchannel/${channelId}`, ``);
  }

  public removeUserFromChannel(userId: number, channelId: number): Observable<any>
  {
    return this.http.put<any>(`/users/${userId}/removechannel/${channelId}`, ``);
  }

  public updateUserActiveRole(username: string, role: string): Observable<any>
  {
    return this.http.put<any>(`/users/role/${username}/${role}`, '');
  }

  public updateUserActiveOrganization(username: string, organizationId: number): Observable<any>
  {
    return this.http.put<any>(`/users/organization/${username}/${organizationId}`, '');
  }

  /**
   * Get Flowable users who are member of a group.
   *
   * @param group Flowable group ID.
   */
  public getFlowableUsersByGroup(group: string): Observable<any[]>
  {
    return this.http.get<any[]>(`/users/flowable/${group}`);
  }

  public getPhotoUser(username: string): Observable<any>
  {
    return this.http.get<any>(`/users/photo/${username}`);
  }

  /**
   * Provide Password Meter Colors shared within User Account and User Registration.
   */
  public getPasswordMeterColors(): any[]
  {
    return ['#FF0000', '#F7B773', '#FF6D00', '#AEEA00', '#458C88'];
  }

  /**
   * Resend Registration Email for the userId.
   *
   * @param userId User Id
   */
  public resendRegistrationEmail(userId: number): Observable<any>
  {
    return this.http.post(`/users/resendregistration/${userId}`, null);
  }

  /**
   * Send Forgot Password Email for the userId.
   *
   * @param data User Id
   */
  public sendForgotPasswordEmail(email: string): Observable<any>
  {
    const credentials = new ForgotPassword();
    credentials.username = '';
    credentials.password = '';
    credentials.email = email;
    return this.http.post(`/unauthenticated/registration/forgotpassword`, credentials);
  }

  /**
   * Send Forgot Username Email for the email. 
   */
  public sendForgotUsernameEmail(email: string): Observable<any>
  {
    return this.http.post(`/unauthenticated/registration/forgotusername`, email);
  }

  /**
   * Get users related with a member id.
   *
   * @param member Id of the member.
   */
  public getUsersByMember(memberId: number): Observable<any[]>
  {
    return this.http.get<any>(`/users/member/${memberId}`);
  }

  public getUserRoleAccess(userId: number, roleAccess: string): Observable<any>
  {
    return this.http.get(`/users/${userId}/role/access?roleAccess=${roleAccess}`);
  }

  /**
   * Get a member pinned resources.
   *
   * @param id User id
   * @param memberId Member id
   * @param loadPhoto Indicate if loading user photo or not.
   */
  public getMemberPinnedResources(id: number, memberId: number, loadPhoto: boolean): Observable<User>
  {
    return this.http.get<User>(`/users/${id}?memberId=${memberId}&loadPhoto=${loadPhoto}`);
  }

  /**
   * Get non users related with a member id.
   *
   * @param member Id of the member.
   */
  public getNonUsersByMember(memberId: number, rolesIds: string): Observable<any[]>
  {
    if (rolesIds !== null)
    {
      return this.http.get<any>(`/people/nonusers/member/${memberId}?rolesIds=${rolesIds}`);
    }
    else
    {
      return this.http.get<any>(`/people/nonusers/member/${memberId}`);
    }
  }

  /**
   * Get users not related with a member id.
   *
   * @param member Id of the member.
   */
  public getUsersByNotMember(memberId: number): Observable<User[]>
  {
    return this.http.get<any>(`/users/notmember/${memberId}`);
  }

  /**
   * Get users related with a member id.
   *
   * @param member Id of the member.
   */
  public getTeamUsersByCase(caseId: number): Observable<any[]>
  {
    return this.http.get<any>(`/users/caseteam/user/${caseId}`);
  }

  /**
   * Get non users related with a member id.
   *
   * @param member Id of the member.
   */
  public getTeamNonUsersByCase(caseId: number): Observable<any[]>
  {
    return this.http.get<any>(`/people/caseteam/nonuser/${caseId}`);
  }

  /**
   * Get users not related with a case id.
   *
   * @param case Id of the case.
   */
  public getUsersByNotCase(caseId: number): Observable<User[]>
  {
    return this.http.get<any>(`/users/notcase/${caseId}`);
  }

  /**
   * Get the Role text to display.
   *
   * @param role Role string.
   */
  public getRoleText(role: string): string
  {
    let text = role;
    const roleObject = this.roles.find(x => x.value === role);
    if (roleObject !== undefined)
    {
      text = roleObject.text;
    }
    return text;
  }

  /**
   * Get the User full name to display.
   *
   * @param users All users.
   * @param value User value string.
   */
  public getUserText(users: any, value: string): string
  {
    let text = value;
    const userObject = users.find(x => x.id === parseInt(value, 10));
    if (userObject !== undefined)
    {
      text = userObject.fullName;
    }
    return text;
  }

  /**
   * Get the Language text to display.
   *
   * @param language Language value.
   */
  public getLanguageText(language: string): string
  {
    let text = language;
    const languageObject = this.preferredLanguages.find(x => x.value === language);
    if (languageObject !== undefined)
    {
      text = languageObject.text;
    }
    return text;
  }

  /**
   * Convert a list of Roles to roles texts.
   *
   * @param roles List of Roles.
   */
  public convertRolesToDisplayTexts(roles: string): string
  {
    const res = roles.split(',');
    const rolesText = [];
    res.forEach(role =>
    {
      const roleText = this.getRoleText(role);
      if (roleText !== '')
      {
        rolesText.push(roleText);
      }
    });
    return rolesText.join(', ');
  }

  public convertUsersToDisplayTexts(allUsers: any, values: string): string
  {
    const listUsers = values.split(',');
    const usersText = [];
    listUsers.forEach(user =>
    {
      const userText = this.getUserText(allUsers, user);
      if (userText !== '')
      {
        usersText.push(userText);
      }
    });
    return usersText.join(', ');
  }

  /**
   * Check if a role is within the comma separated roles.
   *
   * @param role Check if role is within the roles list.
   * @param roles Roles to check.
   */
  public isRoleInList(role: string, roles: string): boolean
  {
    const res = (roles !== undefined) ? roles.split(',') : [];
    return res.findIndex(x => x === role) > -1;
  }

  /**
   * Check if user id is in list
   *
   * @param id User id to check
   * @param ids List of user ids
   */
  public isUserInList(id: string, ids: string): boolean
  {
    const res = (ids !== undefined) ? ids.split(',') : [];
    return res.findIndex(x => parseInt(x, 10) === parseInt(id, 10)) > -1;
  }

  /**
   * Check if the direct address exists.
   *
   * @param directAddress Direct address to validate
   */
  public directAddressExists(directAddress: string): Observable<any>
  {
    return this.http.get<any>(`/users/direct/${directAddress}/exists`);
  }

  /**
   * Get all online users (minimal fields: id, name, photo, username, role, organizationName).
   *
   * @returns Online user list wrapper.
   */
  public getAllOnlineUsers(): Observable<OnlineUserListResponse>
  {
    if (!this.onlineUsersInflightRequest)
    {
      this.onlineUsersInflightRequest = this.http.get<OnlineUserListResponse>(`/users/online`).pipe(
        shareReplay(1),
        finalize(() =>
        {
          this.onlineUsersInflightRequest = null;
        }));
    }

    return this.onlineUsersInflightRequest;
  }

  /**
   * Save the community services to the selected email.
   *
   * @param data Email data.
   */
  public sendResourcesByEmail(data: SendEmailSMSContent): Observable<any>
  {
    return this.http.post('/public/community/service/email/resources', data);
  }

  /**
   * Save the community services to the selected phone number.
   *
   * @param data Sms data.
   */
  public sendResourcesBySMS(data: SendEmailSMSContent): Observable<any>
  {
    return this.http.post('/public/community/service/sms/resources', data);
  }

  /**
   * Check if user has active cases, tasks or is assigned to a Program or Channel
   *
   * @param id User Id
   */
  public userInUseCounts(id: number): Observable<any>
  {
    return this.http.get<any>(`/users/inuse/counts/${id}`);
  }

  /**
   * Get an array of Channels / Programs assigned to a user
   */
  public getProgramsChannelsPerUser(id: number): Observable<any[]>
  {
    return this.http.get<any>(`/users/programs/channels/${id}`);
  }

  /**
   * Get the current logged in user password expiration
   */
  public getUserPasswordExpiration(): Observable<UserPasswordExpiration>
  {
    return this.http.get<UserPasswordExpiration>(`/users/password/check`);
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    setTimeout(() =>
    {
      this.translate.get(['USER-ROLES', 'ACCOUNT-LANGUAGES']).subscribe((res: string) =>
      {
        let splitted = res['USER-ROLES'].split(',');
        for (let i = 0; i < splitted.length; i++)
        {
          if (this.roles.length > i)
          {
            this.roles[i].text = splitted[i];
          }
        }

        // Preferred Languages
        splitted = res['ACCOUNT-LANGUAGES'].split(',');
        for (let i = 0; i < splitted.length; i++)
        {
          if (this.preferredLanguages.length > i)
          {
            this.preferredLanguages[i].text = splitted[i];
          }
        }
      });
    });
  }
}
